import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Dashboard.css';

interface SavedDocument {
    id: number;
    document_type: string;
    document_id: number;
    title: string;
    created_at: string;
}

const DashboardFavorites: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isArabic = i18n.language === 'ar';
    const [favorites, setFavorites] = useState<SavedDocument[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFavorites = async () => {
            try {
                const res = await api.get('/favorites');
                setFavorites(res.data || []);
            } catch (err) {
                console.error('Error fetching favorites:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchFavorites();
    }, []);

    const removeFavorite = async (docType: string, docId: number) => {
        if (!window.confirm(isArabic ? 'هل تريد إزالة هذا المستند من المفضلة؟' : 'Voulez-vous retirer ce document des favoris ?')) return;
        try {
            await api.delete('/favorites', { data: { document_type: docType, document_id: docId } });
            setFavorites(prev => prev.filter(f => !(f.document_type === docType && f.document_id === docId)));
        } catch (err) {
            console.error('Error removing favorite:', err);
        }
    };

    const getDocTypeIcon = (type: string) => {
        switch (type) {
            case 'code_article': return 'bi-file-earmark-text';
            case 'jurisprudence': return 'bi-hammer';
            case 'joarticle': return 'bi-journal-text';
            case 'livre': return 'bi-book';
            case 'these': return 'bi-mortarboard';
            default: return 'bi-file';
        }
    };

    const getDocTypeLabel = (type: string) => {
        const labels: Record<string, { fr: string; ar: string }> = {
            code_article: { fr: 'Article de Code', ar: 'مادة قانونية' },
            jurisprudence: { fr: 'Jurisprudence', ar: 'اجتهاد قضائي' },
            joarticle: { fr: 'Textes officiels', ar: 'جريدة رسمية' },
            livre: { fr: 'Livre', ar: 'كتاب' },
            these: { fr: 'Thèse', ar: 'أطروحة' }
        };
        return labels[type] ? (isArabic ? labels[type].ar : labels[type].fr) : type;
    };

    const getDocTypeBadgeClass = (type: string) => {
        const classes: Record<string, string> = {
            code_article: 'badge-code',
            jurisprudence: 'badge-jps',
            joarticle: 'badge-jo',
            livre: 'badge-livre',
            these: 'badge-these'
        };
        return classes[type] || 'badge-default';
    };

    const getDocDetailLink = (type: string, id: number) => {
        const routes: Record<string, string> = {
            code_article: 'code-article',
            jurisprudence: 'jurisprudence',
            joarticle: 'jo',
            livre: 'livres',
            these: 'theses'
        };
        const route = routes[type] || 'code-article';
        return `/${i18n.language}/detail/${route}/${id}`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(isArabic ? 'ar-DZ' : 'fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const truncateText = (text: string, maxLength: number = 50) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="dashboard-page">
            <div className="container py-4">
                <div className="dashboard-header mb-4">
                    <h1 className="dashboard-title">
                        <i className="bi bi-star-fill me-3 text-warning"></i>
                        {isArabic ? 'المستندات المفضلة' : 'Documents Favoris'}
                    </h1>
                    <p className="text-muted">
                        {isArabic
                            ? 'قائمة المستندات القانونية التي قمت بحفظها'
                            : 'Liste des documents juridiques que vous avez enregistrés'}
                    </p>
                </div>

                {/* Favorites Section */}
                <section className="dashboard-section mb-5">
                    <div className="section-header">
                        <h2 className="section-title">
                            <i className="bi bi-bookmark-star me-2 text-warning"></i>
                            {isArabic ? 'المفضلات' : 'Mes Favoris'}
                        </h2>
                        <span className="badge bg-warning text-dark rounded-pill">{favorites.length}</span>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : favorites.length === 0 ? (
                        <div className="empty-state">
                            <i className="bi bi-star empty-icon"></i>
                            <p>{isArabic ? 'لا توجد مستندات مفضلة' : 'Aucun document favori'}</p>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate(`/${i18n.language}/search`)}
                            >
                                <i className="bi bi-search me-1"></i>
                                {isArabic ? 'بدء البحث' : 'Commencer une recherche'}
                            </button>
                        </div>
                    ) : (
                        <div className="cards-grid">
                            {favorites.map(doc => (
                                <div key={doc.id} className="dashboard-card search-card">
                                    <div className="card-header-row">
                                        <span className={`type-badge ${getDocTypeBadgeClass(doc.document_type)}`}>
                                            <i className={`bi ${getDocTypeIcon(doc.document_type)} me-1`}></i>
                                            {getDocTypeLabel(doc.document_type)}
                                        </span>
                                        <div className="card-actions">
                                            <Link
                                                to={getDocDetailLink(doc.document_type, doc.document_id)}
                                                className="btn-action btn-run"
                                                title={isArabic ? 'عرض' : 'Voir'}
                                            >
                                                <i className="bi bi-eye-fill"></i>
                                            </Link>
                                            <button
                                                className="btn-action btn-delete"
                                                onClick={() => removeFavorite(doc.document_type, doc.document_id)}
                                                title={isArabic ? 'إزالة' : 'Retirer'}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <Link
                                        to={getDocDetailLink(doc.document_type, doc.document_id)}
                                        className="card-body-content text-decoration-none"
                                    >
                                        <p className="query-preview" title={doc.title}>
                                            {truncateText(doc.title, 60)}
                                        </p>
                                        <div className="card-meta">
                                            <i className="bi bi-clock me-1"></i>
                                            {formatDate(doc.created_at)}
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default DashboardFavorites;
